import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Play, Pause, RotateCcw, Clock } from "lucide-react";

/**
 * Pomodoro-style focus timer with 15 / 25 / 45 min presets,
 * a custom block title, and three audio cues (start, 5-min warning,
 * completion). Plays sound only when soundEnabled.
 */
const FocusTimer = ({ soundEnabled, playSound }) => {
  const [duration, setDuration] = useState(25);
  const [timeLeft, setTimeLeft] = useState(duration * 60);
  const [isRunning, setIsRunning] = useState(false);
  const [blockTitle, setBlockTitle] = useState("Focus Block");
  const intervalRef = useRef(null);

  const progress = ((duration * 60 - timeLeft) / (duration * 60)) * 100;

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            if (soundEnabled) playSound("complete");
            toast.success(`${blockTitle} completed!`);
            return 0;
          }
          if (prev === 301 && soundEnabled) {
            playSound("warning");
            toast.info("5 minutes remaining", { duration: 3000 });
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft, soundEnabled, playSound, blockTitle]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStart = () => {
    if (soundEnabled) playSound("start");
    setIsRunning(true);
  };

  const presets = [
    { label: "15m", value: 15 },
    { label: "25m", value: 25 },
    { label: "45m", value: 45 },
  ];

  return (
    <Card className="bento-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" />
          Focus Timer
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          value={blockTitle}
          onChange={(e) => setBlockTitle(e.target.value)}
          placeholder="What are you focusing on?"
          className="text-sm"
          data-testid="focus-block-title"
        />

        <div className="text-center">
          <div className={`timer-display text-primary ${isRunning && timeLeft <= 60 ? "animate-countdown-pulse" : ""}`}>
            {formatTime(timeLeft)}
          </div>
          <Progress value={progress} className="mt-2 h-2" />
        </div>

        <div className="flex justify-center gap-2">
          {presets.map((preset) => (
            <Button
              key={preset.value}
              variant={duration === preset.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setDuration(preset.value);
                setTimeLeft(preset.value * 60);
                setIsRunning(false);
              }}
              disabled={isRunning}
            >
              {preset.label}
            </Button>
          ))}
        </div>

        <div className="flex justify-center gap-2">
          {!isRunning ? (
            <Button onClick={handleStart} className="w-24" data-testid="timer-start-btn">
              <Play className="w-4 h-4 mr-1" /> Start
            </Button>
          ) : (
            <Button onClick={() => setIsRunning(false)} variant="outline" className="w-24">
              <Pause className="w-4 h-4 mr-1" /> Pause
            </Button>
          )}
          <Button
            onClick={() => {
              setIsRunning(false);
              setTimeLeft(duration * 60);
            }}
            variant="ghost"
            size="icon"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FocusTimer;
